import React from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Outlet, Link } from "react-router-dom";
import { Box, Avatar } from "@mui/material";
import { SignInCard, PageError, TypedLottieSpinner } from "pymble-ui/components";
import { useMyDetails } from "../hooks/useMyDetails";
import { loginRequest, DEV_BYPASS_AUTH } from "../config";
import { useProfilePhoto } from "../hooks/useProfilePhoto";
import AccessDenied from "../components/AccessDenied";
import "./layout.css";

const AuthGate: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
	const { isLoading, isError, hasAdminAccess } = useMyDetails();

	if (isLoading)
		return (
			<Box display="flex" flexDirection="column" alignItems="center" mt="20vh">
				<TypedLottieSpinner type="General" label="Checking access..." size={96} />
			</Box>
		);

	if (isError)
		return (
			<Box display="flex" justifyContent="center" mt="20vh">
				<PageError error="Error loading user details. Please refresh." />
			</Box>
		);

	if (!hasAdminAccess) return <AccessDenied />;

	return <>{children}</>;
};

function timeGreeting(): string {
	const h = new Date().getHours();
	return h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening";
}

export const PageLayout: React.FC = () => {
	const { instance, accounts, inProgress } = useMsal();
	const photoUrl = useProfilePhoto();
	const isAuthenticated = useIsAuthenticated();

	const handlingRedirect = inProgress === InteractionStatus.HandleRedirect ||
		inProgress === InteractionStatus.Startup;

	const handleLogin = () => {
		if (inProgress !== InteractionStatus.None) return;
		instance.loginRedirect(loginRequest).catch((err) => {
			if (err?.errorCode === "interaction_in_progress") {
				// Stale lock left in sessionStorage from an incomplete redirect — clear it and retry
				Object.keys(sessionStorage)
					.filter((k) => k.includes("interaction.status"))
					.forEach((k) => sessionStorage.removeItem(k));
				instance.loginRedirect(loginRequest).catch(console.error);
			} else {
				console.error(err);
			}
		});
	};

	if (DEV_BYPASS_AUTH) {
		return (
			<>
				<nav className="pa-navbar">
					<div className="pa-navbar-left">
						<Link to="/" className="pa-navbar-brand">
							<img src="/assets/icon/icon.png" alt="Pymble" className="pa-logo" />
						</Link>
					</div>
					<div className="pa-navbar-center">
						<h2 className="pa-nav-title">Pastoral Admin</h2>
					</div>
					<div className="pa-navbar-right">
						<span className="pa-nav-greeting">{timeGreeting()}, Dev</span>
					</div>
				</nav>
				<Outlet />
			</>
		);
	}

	return (
		<>
			{isAuthenticated ? (
				<>
					<nav className="pa-navbar">
						<div className="pa-navbar-left">
							<Link to="/" className="pa-navbar-brand">
								<img src="/assets/icon/icon.png" alt="Pymble" className="pa-logo" />
							</Link>
						</div>
						<div className="pa-navbar-center">
							<h2 className="pa-nav-title">Pastoral Admin</h2>
						</div>
						<div className="pa-navbar-right">
							{accounts.length > 0 && (
								<>
									<span className="pa-nav-greeting">
										{timeGreeting()}, {accounts[0].name?.split(" ")[0]}
									</span>
									<Avatar
										src={photoUrl ?? undefined}
										sx={{ width: 36, height: 36, bgcolor: "#c8102e", fontSize: 14, cursor: "pointer" }}
										onClick={() => instance.logoutRedirect()}
										title="Sign out"
									>
										{accounts[0].name?.charAt(0) ?? "?"}
									</Avatar>
								</>
							)}
						</div>
					</nav>
					<AuthGate>
						<Outlet />
					</AuthGate>
				</>
			) : handlingRedirect ? (
				<Box display="flex" justifyContent="center" mt="20vh">
					<TypedLottieSpinner type="General" label="Signing in..." size={96} />
				</Box>
			) : (
				<div className="pa-login-screen">
					<SignInCard
						appName="Pastoral Admin"
						welcomeText="Welcome to"
						description="Sign in to access the pastoral management system."
						onLogin={handleLogin}
					/>
				</div>
			)}
		</>
	);
};
